/** Shared pathfinding utilities: waypoint following and intersection finding for AI modules. */
import { findPath, type PathfindOutcome } from '../utils/Pathfinder';
import { distance, distanceSq, worldToTile, tileToWorld, scaledNormalize } from '../utils/Math';
import type { Position } from '../utils/Types';
import { MovementResult, ZERO_MOVEMENT, PATH_RECALC_INTERVAL, PATH_WAYPOINT_THRESHOLD_SQ, DIR_QUANTIZE_THRESHOLD, CARDINAL_DIRS, tileBlocksMovement, tileBlocksMovementStrict } from './constants';
import { ConfigLoader } from '../systems/ConfigLoader';
import { DebugLog } from '../debug/DebugLog';

/**
 * Shared pathfinding utilities: waypoint following, cached A* pathfinding,
 * and intersection scanning.
 *
 * Logs path recalculation details (budget, outcome, nodes searched, waypoint count)
 * under the `pathfinding` debug category — separate from the `ai` category that
 * handles state machine, aggro, and detection events.
 */
export class PathHelper {
  private path: Position[] | null = null;
  private pathIndex: number = 0;
  private pathRecalcTimer: number = 0;

  private readonly groundLayer: Phaser.Tilemaps.TilemapLayer;

  /** Numeric seed for path cost perturbation — derived from the owning enemy's instance tag. */
  private readonly pathSeed: number;

  /** Bound logger for the pathfinding debug category. */
  private readonly log: (msg: string) => void;

  /** Diagnostics from the most recent path calculation. */
  private _lastOutcome: PathfindOutcome = 'same_tile';
  private _lastNodesSearched = 0;
  private _lastPathLength = 0;

  constructor(groundLayer: Phaser.Tilemaps.TilemapLayer, pathSeed: number = 0, tag: string = '') {
    this.groundLayer = groundLayer;
    this.pathSeed = pathSeed;
    this.log = tag ? DebugLog.logger('pathfinding', tag) : () => {};
  }

  /** Outcome of the most recent A* search (found, budget_exhausted, unreachable, goal_blocked). */
  get lastOutcome(): PathfindOutcome { return this._lastOutcome; }

  /** How many nodes the most recent A* search expanded. */
  get lastNodesSearched(): number { return this._lastNodesSearched; }

  /** Number of waypoints in the most recently computed path. */
  get lastPathLength(): number { return this._lastPathLength; }

  /** Current A* path waypoints (empty if no path). For debug visualization. */
  get currentPath(): readonly Position[] { return this.path ?? []; }

  /** Index of the next waypoint being followed. */
  get currentPathIndex(): number { return this.pathIndex; }

  /** Clear the cached path and force recalculation next frame. */
  clearPath(): void {
    this.path = null;
    this.pathRecalcTimer = 0;
  }

  /** Follow waypoints toward current waypoint. Returns velocity + progress info. */
  followWaypoints(
    waypoints: Position[],
    startIndex: number,
    entityX: number, entityY: number,
    speed: number,
  ): { vx: number; vy: number; newIndex: number; arrived: boolean } {
    let idx = startIndex;

    while (idx < waypoints.length) {
      const wp = waypoints[idx];
      if (distanceSq(entityX, entityY, wp.x, wp.y) < PATH_WAYPOINT_THRESHOLD_SQ) {
        idx++;
      } else {
        break;
      }
    }

    if (idx >= waypoints.length) {
      return { vx: 0, vy: 0, newIndex: idx, arrived: true };
    }

    const wp = waypoints[idx];
    const wdx = wp.x - entityX;
    const wdy = wp.y - entityY;
    const vel = scaledNormalize(wdx, wdy, speed);
    return {
      vx: vel.x,
      vy: vel.y,
      newIndex: idx,
      arrived: false,
    };
  }

  /**
   * Follow a cached A* path toward a target. Recalculates periodically.
   * Calls onArrival only when the target is actually reached (path fully walked).
   * Budget exhaustion returns zero movement without calling onArrival — callers
   * should use their own stuck detection to handle prolonged failure.
   *
   * Path recalculation details (budget, outcome, nodes searched, waypoint count)
   * are logged directly under the `pathfinding` debug category.
   */
  pathfindToward(
    entityX: number, entityY: number,
    targetX: number, targetY: number,
    delta: number,
    speed: number,
    onArrival?: () => void,
    maxNodes?: number,
    reversed?: boolean,
  ): MovementResult {
    this.pathRecalcTimer -= delta;

    if (!this.path || this.pathRecalcTimer <= 0) {
      // Apply path cost perturbation on long-distance paths to create route diversity between enemies
      const { perturbation, minDistanceTiles } = ConfigLoader.physics.pathDiversity;
      const tileDist = distance(worldToTile(entityX), worldToTile(entityY), worldToTile(targetX), worldToTile(targetY));
      const usePerturbation = this.pathSeed !== 0 && perturbation > 0 && tileDist >= minDistanceTiles;
      const budget = maxNodes ?? 200;

      // Reverse mode: calculate path from target to entity and reverse it.
      // When stuck against an obstacle A* can't see (physics collider without
      // tile data), the T→A path approaches from a direction that successfully
      // reaches the entity — reversed, those opening steps lead away from the
      // invisible blocker along a clear corridor.
      const pathResult = reversed
        ? findPath(
            this.groundLayer, targetX, targetY, entityX, entityY, budget,
            usePerturbation ? this.pathSeed : undefined,
            usePerturbation ? perturbation : undefined,
          )
        : findPath(
            this.groundLayer, entityX, entityY, targetX, targetY, budget,
            usePerturbation ? this.pathSeed : undefined,
            usePerturbation ? perturbation : undefined,
          );

      this.pathIndex = 0;
      this.pathRecalcTimer = PATH_RECALC_INTERVAL;
      this._lastOutcome = pathResult.outcome;
      this._lastNodesSearched = pathResult.nodesSearched;

      if (!pathResult.path || pathResult.path.length === 0) {
        this.path = [];
        this._lastPathLength = 0;
        this.log(`recalc (${Math.round(entityX)},${Math.round(entityY)})→(${Math.round(targetX)},${Math.round(targetY)}) budget=${budget} → ${pathResult.outcome} (${pathResult.nodesSearched} nodes${reversed ? ', reversed' : ''})`);
        return ZERO_MOVEMENT;
      }

      if (reversed) {
        pathResult.path.reverse();
        // Append the target tile center — the reversed path ends one tile short
        // because findPath skips the "start" tile (which in reverse is the target).
        pathResult.path.push({
          x: tileToWorld(worldToTile(targetX)),
          y: tileToWorld(worldToTile(targetY)),
        });
      }

      this.path = pathResult.path;
      this._lastPathLength = this.path.length;
      this.log(`recalc (${Math.round(entityX)},${Math.round(entityY)})→(${Math.round(targetX)},${Math.round(targetY)}) budget=${budget} → ${pathResult.outcome} (${pathResult.nodesSearched} nodes, ${this.path.length} waypoints${usePerturbation ? ', perturbed' : ''}${reversed ? ', reversed' : ''})`);
    }

    if (!this.path || this.path.length === 0) {
      return ZERO_MOVEMENT;
    }

    const wp = this.followWaypoints(this.path, this.pathIndex, entityX, entityY, speed);
    this.pathIndex = wp.newIndex;

    if (wp.arrived) {
      this.path = null;
      this.log(`arrived at (${Math.round(targetX)},${Math.round(targetY)})`);
      if (onArrival) onArrival();
      return ZERO_MOVEMENT;
    }

    return { vx: wp.vx, vy: wp.vy, wantsAttack: false };
  }

  /** Find the best adjacent tile to nudge toward when physically stuck.
   *  Checks the four cardinal neighbors for strict walkability (no walls,
   *  no buffer zones), and returns the center of the one closest to the
   *  target. Returns null if all neighbors are blocked or buffered. */
  findNudgeTarget(entityX: number, entityY: number, targetX: number, targetY: number): Position | null {
    const tx = worldToTile(entityX);
    const ty = worldToTile(entityY);

    let bestPos: Position | null = null;
    let bestDist = Infinity;

    for (const [dx, dy] of CARDINAL_DIRS) {
      const nx = tx + dx;
      const ny = ty + dy;
      const tile = this.groundLayer.getTileAt(nx, ny);
      if (tileBlocksMovementStrict(tile)) continue;

      const wx = tileToWorld(nx);
      const wy = tileToWorld(ny);
      const d = distance(wx, wy, targetX, targetY);
      if (d < bestDist) {
        bestDist = d;
        bestPos = { x: wx, y: wy };
      }
    }

    return bestPos;
  }

  /**
   * Walk tiles in a direction until hitting an intersection (3+ open neighbors) or wall.
   */
  findNextIntersection(fromX: number, fromY: number, dirX: number, dirY: number): Position | null {
    let tx = worldToTile(fromX);
    let ty = worldToTile(fromY);

    const sx = dirX > DIR_QUANTIZE_THRESHOLD ? 1 : dirX < -DIR_QUANTIZE_THRESHOLD ? -1 : 0;
    const sy = dirY > DIR_QUANTIZE_THRESHOLD ? 1 : dirY < -DIR_QUANTIZE_THRESHOLD ? -1 : 0;
    if (sx === 0 && sy === 0) return null;

    for (let i = 0; i < 20; i++) {
      tx += sx;
      ty += sy;

      const tile = this.groundLayer.getTileAt(tx, ty);
      if (tileBlocksMovement(tile)) {
        return {
          x: tileToWorld(tx - sx),
          y: tileToWorld(ty - sy),
        };
      }

      let openNeighbors = 0;
      for (const [cx, cy] of CARDINAL_DIRS) {
        const neighbor = this.groundLayer.getTileAt(tx + cx, ty + cy);
        if (!tileBlocksMovement(neighbor)) openNeighbors++;
      }

      if (openNeighbors >= 3) {
        return { x: tileToWorld(tx), y: tileToWorld(ty) };
      }
    }

    return { x: tileToWorld(tx), y: tileToWorld(ty) };
  }
}
